import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaArrowLeft, FaChartLine, FaList, FaCheckCircle, FaFileDownload } from 'react-icons/fa';
import { 
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, 
  Tooltip, CartesianGrid, ReferenceLine 
} from 'recharts';
import './HistoryTracking.css';

const HistoryTracking = () => {
  const { i18n } = useTranslation();
  const currentLang = i18n.language || 'hi';
  const isHindi = currentLang === 'hi';

  const [viewMode, setViewMode] = useState('chart'); // 'chart' | 'list'

  const historyData = [
    { date: '10 May', date_hi: '10 मई', score: 32, level: 'Low', level_hi: 'कम', reason_hi: 'सामान्य स्थिति', reason_en: 'Normal conditions' },
    { date: '13 May', date_hi: '13 मई', score: 46, level: 'Moderate', level_hi: 'मध्यम', reason_hi: 'पानी', reason_en: 'Water stress' },
    { date: '16 May', date_hi: '16 मई', score: 61, level: 'Moderate', level_hi: 'मध्यम', reason_hi: 'पानी, मिट्टी', reason_en: 'Water, Soil' },
    { date: '19 May', date_hi: '19 मई', score: 72, level: 'High', level_hi: 'उच्च', reason_hi: 'पानी, मार्केट', reason_en: 'Water, Market' },
    { date: '22 May', date_hi: '22 मई', score: 78, level: 'High', level_hi: 'उच्च', reason_hi: 'पानी, मौसम', reason_en: 'Water, Weather shock' },
    { date: '25 May', date_hi: '25 मई', score: 64, level: 'Moderate', level_hi: 'मध्यम', reason_hi: 'पानी, मार्केट (सिंचाई के बाद)', reason_en: 'Water, Market (After irrigation)' },
    { date: '28 May', date_hi: '28 मई', score: 42, level: 'Moderate', level_hi: 'मध्यम', reason_hi: 'पानी, मार्केट (सुधार जारी)', reason_en: 'Water, Market (Recovering)' }
  ];

  return (
    <div className="fd-history-page">
      <div className="fd-history-container">
        {/* Header Navigation */}
        <div className="fd-history-header-row">
          <Link to="/dashboard" className="fd-hist-back-btn">
            <FaArrowLeft />
            <span>{isHindi ? 'वापस' : 'Back'}</span>
          </Link>
          <h2>{isHindi ? 'रिस्क इतिहास और ट्रैकिंग' : 'Risk History & Outcome Tracking'}</h2>
          <button className="fd-hist-export-btn" onClick={() => window.print()}>
            <FaFileDownload />
            <span>{isHindi ? 'ऑडिट लॉग' : 'Export Log'}</span>
          </button>
        </div>

        {/* View Switcher Bar */}
        <div className="fd-view-toggle-card fd-card">
          <div className="fd-view-tabs">
            <button 
              className={`fd-view-tab ${viewMode === 'chart' ? 'active' : ''}`}
              onClick={() => setViewMode('chart')}
            >
              <FaChartLine />
              <span>{isHindi ? 'चार्ट व्यू' : 'Risk Trend Chart'}</span>
            </button>
            <button 
              className={`fd-view-tab ${viewMode === 'list' ? 'active' : ''}`}
              onClick={() => setViewMode('list')}
            >
              <FaList />
              <span>{isHindi ? 'लिस्ट व्यू' : 'Risk Records'}</span>
            </button>
          </div>
        </div>

        {/* Chart View Panel */}
        {viewMode === 'chart' && (
          <div className="fd-chart-panel fd-card">
            <div className="fd-chart-panel-hdr">
              <h3>{isHindi ? 'डिस्ट्रेस स्कोर ट्रेंड (10 मई - 28 मई)' : 'Distress Score Trajectory (10 May - 28 May)'}</h3>
              <div className="fd-chart-legend">
                <span className="fd-leg-item"><span className="fd-leg-dot green"></span>{isHindi ? '0-40 कम जोखिम' : '0-40 Low'}</span>
                <span className="fd-leg-item"><span className="fd-leg-dot amber"></span>{isHindi ? '41-70 मध्यम' : '41-70 Moderate'}</span>
                <span className="fd-leg-item"><span className="fd-leg-dot red"></span>{isHindi ? '71-100 उच्च' : '71-100 High'}</span>
              </div>
            </div>

            <div className="fd-history-chart-wrap">
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={historyData} margin={{ top: 20, right: 20, left: -20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                  <XAxis 
                    dataKey={isHindi ? "date_hi" : "date"} 
                    tick={{ fontSize: 12, fill: '#64748b' }} 
                    axisLine={{ stroke: '#cbd5e1' }}
                  />
                  <YAxis 
                    domain={[0, 100]} 
                    ticks={[0, 25, 50, 75, 100]}
                    tick={{ fontSize: 12, fill: '#64748b' }} 
                    axisLine={{ stroke: '#cbd5e1' }}
                  />
                  <Tooltip 
                    contentStyle={{ background: '#ffffff', borderRadius: '10px', border: '1px solid #cbd5e1', fontSize: '13px' }} 
                  />
                  <ReferenceLine y={70} stroke="#ef4444" strokeDasharray="4 4" label={{ value: 'High Threshold (70)', fill: '#ef4444', fontSize: 10 }} />
                  <ReferenceLine y={40} stroke="#10b981" strokeDasharray="4 4" label={{ value: 'Safe Threshold (40)', fill: '#10b981', fontSize: 10 }} />
                  <Line 
                    type="monotone" 
                    dataKey="score" 
                    stroke="#065f46" 
                    strokeWidth={3} 
                    dot={{ fill: '#064e3b', r: 5 }} 
                    activeDot={{ r: 8, stroke: '#10b981', strokeWidth: 2 }} 
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>

            <div className="fd-success-story-banner">
              <FaCheckCircle className="fd-ss-icon" />
              <p>
                {isHindi 
                  ? 'सफलता की कहानी: Fasal Drishti ने 78/100 पर उच्च जोखिम का पहले ही पता लगाया, किसान ने 1-2 दिन में सिंचाई की और जोखिम घटकर 42/100 पर आ गया।'
                  : 'Outcome verified: Platform detected high distress at 78/100 early, recommended irrigation intervention, and risk successfully reduced to 42/100.'}
              </p>
            </div>
          </div>
        )}

        {/* List View / Previous Records Table */}
        <div className="fd-records-table-card fd-card">
          <div className="fd-table-header">
            <h3>{isHindi ? 'पिछले रिकॉर्ड (Audit History)' : 'Recent Historical Records'}</h3>
          </div>

          <div className="fd-table-responsive">
            <table className="fd-data-table">
              <thead>
                <tr>
                  <th>{isHindi ? 'तारीख' : 'Date'}</th>
                  <th>{isHindi ? 'रिस्क स्कोर' : 'DRI Score'}</th>
                  <th>{isHindi ? 'जोखिम स्तर' : 'Severity Level'}</th>
                  <th>{isHindi ? 'मुख्य कारण' : 'Contributing Factor(s)'}</th>
                </tr>
              </thead>
              <tbody>
                {historyData.slice().reverse().map((r, idx) => (
                  <tr key={idx}>
                    <td><strong>{isHindi ? r.date_hi : r.date}</strong></td>
                    <td>
                      <span className="fd-table-score">{r.score} / 100</span>
                    </td>
                    <td>
                      <span className={`fd-badge-${r.level === 'High' ? 'high' : (r.level === 'Moderate' ? 'mod' : 'low')}`}>
                        {isHindi ? r.level_hi : r.level}
                      </span>
                    </td>
                    <td>{isHindi ? r.reason_hi : r.reason_en}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HistoryTracking;
